import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  TextField,
  Box,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  Store as StoreIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import config from '../config';
import { useAuth } from '../context/AuthContext';
import DireccionInput from '../components/forms/DireccionInput';

function LocalForm() {
  const [local, setLocal] = useState({
    nombre_local: '',
    direccion_local: '',
    num_puestos: '',
    latitud: null,
    longitud: null,
    telefono_local: '',
    es_default: false,
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const isEditing = Boolean(id);

  useEffect(() => {
    if (isEditing) {
      fetchLocal();
    }
  }, [id, isEditing]);

  const fetchLocal = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${config.API_BASE_URL}/api/locales/${id}`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setLocal(response.data);
    } catch (error) {
      setError('Error al cargar los datos del local');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLocal(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isEditing) {
        await axios.put(`${config.API_BASE_URL}/api/locales/${id}`, local, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
      } else {
        await axios.post(`${config.API_BASE_URL}/api/locales`, local, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
      }
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/home/locales');
      }, 1500);
      
    } catch (error) {
      setError(isEditing ? 'Error al actualizar el local' : 'Error al crear el local');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/home/locales');
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  if (loading && isEditing) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '60vh',
        p: 3
      }}>
        <CircularProgress size={60} sx={{ mb: 2, color: '#1A237E' }} />
        <Typography variant="h6" color="text.secondary">
          Cargando datos del local...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100%',
      p: { xs: 2, sm: 3, md: 4 }
    }}>
      {/* Header con botón volver */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 4
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleGoBack}
            sx={{
              color: '#1A237E',
              '&:hover': {
                backgroundColor: 'rgba(26, 35, 126, 0.04)'
              }
            }}
          >
            Volver
          </Button>
          
          <Box>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              component="h1"
              sx={{ 
                fontWeight: 'bold',
                color: '#1A237E',
                mb: 0.5
              }}
            >
              {isEditing ? 'Editar Local' : 'Crear Local'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEditing ? 'Modifica la información del local' : 'Ingresa los datos del nuevo local'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          Local {isEditing ? 'actualizado' : 'creado'} exitosamente. Redirigiendo...
        </Alert>
      )}

      {/* Formulario */}
      <Paper sx={{ 
        borderRadius: 3,
        overflow: 'hidden',
        border: '1px solid rgba(26, 35, 126, 0.1)',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
        maxWidth: 800,
        mx: 'auto'
      }}>
        {/* Header del formulario */}
        <Box sx={{
          background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
          color: 'white',
          p: { xs: 3, sm: 4 },
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <StoreIcon sx={{ fontSize: { xs: '2rem', sm: '2.5rem' } }} />
          <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 'bold' }}>
            Información del Local
          </Typography>
        </Box>

        {/* Contenido del formulario */}
        <Box sx={{ p: { xs: 3, sm: 4 } }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Información del Local */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ color: '#1A237E', fontWeight: 'bold', mb: 2 }}>
                  Datos del Local
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nombre del Local"
                  name="nombre_local"
                  value={local.nombre_local}
                  onChange={handleChange}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': { borderColor: '#4FC3F7' },
                      '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Número de Puestos"
                  name="num_puestos"
                  type="number"
                  value={local.num_puestos}
                  onChange={handleChange}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': { borderColor: '#4FC3F7' },
                      '&.Mui-focused fieldset': { borderColor: '#1A237E' }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={local.es_default || false}
                        onChange={(e) => setLocal(prev => ({ ...prev, es_default: e.target.checked }))}
                      />
                    }
                    label="Local por defecto (se pre-selecciona en las órdenes)"
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Teléfono del local"
                  name="telefono_local"
                  value={local.telefono_local || ''}
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: 2 }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <DireccionInput
                  label="Dirección del local (con ubicación en mapa)"
                  value={{
                    direccion: local.direccion_local || '',
                    latitud: local.latitud,
                    longitud: local.longitud,
                  }}
                  onChange={(val) => setLocal(prev => ({
                    ...prev,
                    direccion_local: val.direccion || prev.direccion_local,
                    latitud: val.latitud || prev.latitud,
                    longitud: val.longitud || prev.longitud,
                  }))}
                  requiereCoordenadas={false}
                  showContacto={false}
                  showMap={true}
                />
                {local.latitud && local.longitud && (
                  <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
                    ✅ Coordenadas: {Number(local.latitud).toFixed(6)}, {Number(local.longitud).toFixed(6)}
                  </Typography>
                )}
              </Grid>

              {/* Botones de acción */}
              <Grid item xs={12}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2, 
                  justifyContent: 'flex-end',
                  mt: 3,
                  pt: 3,
                  borderTop: '1px solid rgba(26, 35, 126, 0.1)'
                }}>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancel}
                    sx={{
                      borderColor: '#1A237E',
                      color: '#1A237E',
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      '&:hover': {
                        borderColor: '#0D47A1',
                        backgroundColor: 'rgba(26, 35, 126, 0.04)'
                      }
                    }}
                  >
                    Cancelar
                  </Button>
                  
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    disabled={loading}
                    sx={{
                      background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      boxShadow: '0 4px 15px rgba(26, 35, 126, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
                        boxShadow: '0 6px 20px rgba(26, 35, 126, 0.4)',
                        transform: 'translateY(-2px)'
                      },
                      '&:disabled': {
                        background: '#E0E0E0',
                        color: '#999'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {loading ? 'Guardando...' : (isEditing ? 'Actualizar Local' : 'Crear Local')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Paper>
    </Box>
  );
}

export default LocalForm;